import { z } from "zod";

// ---------------------------------------------------------------------------
// Boundary contract for the OpenAI Realtime data-channel events (JSON strings)
// received over WebRTC during voice dictation.
//
// A gpt-realtime-whisper transcription session emits MANY event types
// (session.created, input_audio_buffer.*, etc.); we only act on the three that
// carry transcript text or a failure. `parseTranscriptionEvent` narrows the raw
// wire payload to that closed set and returns `null` for everything else (an
// event we intentionally ignore, not an error to surface).
// ---------------------------------------------------------------------------

// OpenAI-native transcription event (data-channel). The OpenAI transport
// normalizes these to the shared `DictationEvent` (delta → accumulated partial).
export type RawTranscriptEvent =
  | { kind: "delta"; text: string }
  | { kind: "final"; text: string }
  | { kind: "error"; message: string };

const deltaEventSchema = z.object({
  type: z.literal("conversation.item.input_audio_transcription.delta"),
  delta: z.string(),
});

const completedEventSchema = z.object({
  type: z.literal("conversation.item.input_audio_transcription.completed"),
  transcript: z.string(),
});

// Both failure shapes carry an `error` object; `message` is the human-facing text.
const failureErrorSchema = z.object({ message: z.string() });

const transcriptionFailedEventSchema = z.object({
  type: z.literal("conversation.item.input_audio_transcription.failed"),
  error: failureErrorSchema,
});

const sessionErrorEventSchema = z.object({
  type: z.literal("error"),
  error: failureErrorSchema,
});

// Only the `type` discriminator is needed to route; the specific schemas parse
// the rest. Kept separate from a single discriminatedUnion so an unrecognized
// `type` is a clean "ignore" rather than a parse throw.
const envelopeSchema = z.object({ type: z.string() });

/**
 * Parse one raw data-channel message into a `RawTranscriptEvent` for the
 * transcript/failure events we handle, or `null` for malformed JSON and every
 * other (ignored) realtime event type.
 */
export function parseTranscriptionEvent(
  raw: string
): RawTranscriptEvent | null {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }

  const envelope = envelopeSchema.safeParse(json);
  if (!envelope.success) return null;

  switch (envelope.data.type) {
    case "conversation.item.input_audio_transcription.delta": {
      const parsed = deltaEventSchema.safeParse(json);
      return parsed.success ? { kind: "delta", text: parsed.data.delta } : null;
    }
    case "conversation.item.input_audio_transcription.completed": {
      const parsed = completedEventSchema.safeParse(json);
      return parsed.success
        ? { kind: "final", text: parsed.data.transcript }
        : null;
    }
    case "conversation.item.input_audio_transcription.failed": {
      const parsed = transcriptionFailedEventSchema.safeParse(json);
      return parsed.success
        ? { kind: "error", message: parsed.data.error.message }
        : null;
    }
    case "error": {
      const parsed = sessionErrorEventSchema.safeParse(json);
      return parsed.success
        ? { kind: "error", message: parsed.data.error.message }
        : null;
    }
    default:
      return null;
  }
}
